import React, { useEffect, useState } from "react";
import api, { BACKEND_URL, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { FileSpreadsheet, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const COLORS = ["#1D4ED8", "#0284C7", "#059669", "#D97706", "#DC2626", "#7C3AED", "#DB2777", "#0891B2"];

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [overtime, setOvertime] = useState([]);
  const [violations, setViolations] = useState([]);

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/reports/admin"),
        api.get("/timeclock/all?over_buffer_only=true"),
        api.get("/timeclock/violations"),
      ]);
      setStats(r1.data);
      setOvertime(r2.data);
      setViolations(r3.data);
    } catch (err) { toast.error("Fehler beim Laden"); }
  };
  useEffect(() => { load(); }, []);

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/reports/payroll/${fmt}?year=${year}&month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Fehler");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `lohn_${year}_${String(month).padStart(2, "0")}.${fmt === "xlsx" ? "xlsx" : fmt === "csv" ? "csv" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Export fehlgeschlagen"); }
  };

  if (!stats) return null;

  const hoursData = stats.hours_by_user.slice(0, 10);
  const pieData = [
    { name: "Aktive Mitarbeiter", value: stats.total_users },
    { name: "Offene Anträge", value: stats.open_absences },
    { name: "Überstunden ohne Grund", value: stats.over_buffer_count },
    { name: "Verspätungen (Monat)", value: stats.total_delays ?? 0 }
  ];

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div>
        <div className="overline">Verwaltung</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold">Berichte & Analytics</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          ["Aktive Mitarbeiter", stats.total_users, "blue"],
          ["Standorte", stats.total_locations, "green"],
          ["Offene Anträge", stats.open_absences, "amber"],
          ["Überstd. ohne Grund", stats.over_buffer_count, "rose"],
          ["Verspätungen (Monat)", stats.total_delays ?? 0, "rose"],
        ].map(([lbl, val, tone]) => (
          <Card key={lbl} className="border-border/60"><CardContent className="p-5">
            <div className="overline mb-1">{lbl}</div>
            <div className="font-heading text-3xl font-extrabold">{val}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/60">
          <CardHeader><CardTitle className="text-lg font-heading">Stunden pro Mitarbeiter (Monat)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hoursData} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                  <YAxis dataKey="name" type="category" fontSize={11} stroke="hsl(var(--muted-foreground))" width={100} />
                  <Tooltip />
                  <Bar dataKey="hours" name="Reguläre Stunden" fill="#1D4ED8" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="overtime_hours" name="Überstunden" fill="#D97706" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="late_hours" name="Verspätung (Std)" fill="#DC2626" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader><CardTitle className="text-lg font-heading">Systemauslastung</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/60">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg font-heading">Lohnbuchhaltung-Export</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-40" data-testid="report-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-28" data-testid="report-year"><SelectValue /></SelectTrigger>
                <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="report-csv" variant="outline" onClick={() => download("csv")}><FileDown className="w-4 h-4 mr-2" /> CSV</Button>
              <Button data-testid="report-xlsx" variant="outline" onClick={() => download("xlsx")}><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</Button>
              <Button data-testid="report-pdf" onClick={() => download("pdf")}><FileText className="w-4 h-4 mr-2" /> PDF</Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="border-border/60">
        <CardHeader><CardTitle className="text-lg font-heading">Überstunden ohne Begründung</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Mitarbeiter</TableHead><TableHead>Überstunden</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {overtime.slice(0, 10).map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.date}</TableCell>
                  <TableCell>{stats.hours_by_user.find(u => u.user_id === e.user_id)?.name || e.user_id}</TableCell>
                  <TableCell><Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{Math.round(e.overtime_minutes)}m</Badge></TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={async () => { await api.post("/timeclock/request-justify", { entry_id: e.id }); toast.success("Anfrage gesendet"); }}>Begründung anfordern</Button></TableCell>
                </TableRow>
              ))}
              {overtime.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                    Keine unbegründeten Überstunden vorhanden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader><CardTitle className="text-lg font-heading">Verspätungen (Später als Schichtbeginn + 5 Min. Puffer)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Mitarbeiter</TableHead>
                <TableHead>Geplante Schicht</TableHead>
                <TableHead>Stempelzeit</TableHead>
                <TableHead>Verspätung</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {violations.filter(v => v.is_late).slice(0, 15).map((v) => {
                const e = v.entry;
                return (
                  <TableRow key={e.id}>
                    <TableCell>{e.date}</TableCell>
                    <TableCell>{`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.user_id}</TableCell>
                    <TableCell>{e.scheduled_start || "—"}</TableCell>
                    <TableCell>{e.clock_in ? new Date(e.clock_in).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                    <TableCell><Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">{v.late_minutes} Min.</Badge></TableCell>
                  </TableRow>
                );
              })}
              {violations.filter(v => v.is_late).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    Keine Verspätungen verzeichnet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
